import { NavigationServiceV1 } from '@volkswagen-onehub/navigation-service';
import React from 'react';
import { UserInteractionServiceV1 } from '@volkswagen-onehub/user-interaction-service';
import { actionRedirect, normalizeToTimestamp } from './common-helpers';
import { NormalizedVoucher, VoucherStatus } from '@esportal/portal-service';

export class VouchersHelper {
	public static getValidityMessage(
		voucherStatus: VoucherStatus,
		voucherExpirationDate: number | string | Date | null,
		voucherEnjoyed: string | number | Date | null,
	): string {
		enum VoucherStatusMessage {
			'EXPIRED' = 'Vencido',
			'ENJOYED' = 'Disfrutado el #DATE_ENJOYED',
			'TO_ENJOY' = 'Válido hasta el #DATE_EXPIRATION',
			'NO_EXPIRATION' = 'Disponible',
			'TO_ENJOY_SOON' = 'Válido hasta el #DATE_EXPIRATION',
		}

		const messageToUse: string = (VoucherStatusMessage as any)[voucherStatus as string];

		return messageToUse
			.replace('#DATE_ENJOYED', voucherEnjoyed ? this._getDataValidityString(voucherEnjoyed) : '')
			.replace('#DATE_EXPIRATION', voucherExpirationDate ? this._getDataValidityString(voucherExpirationDate) : '');
	}

	public static getVoucherStatusColor(voucherStatus: VoucherStatus): string {
		enum VoucherStatusColor {
			'EXPIRED' = '#e02020',
			'ENJOYED' = '#e02020',
			'TO_ENJOY' = '#029640',
			'NO_EXPIRATION' = '#029640',
			'TO_ENJOY_SOON' = '#ffaa27',
		}

		const colorToUse: string = (VoucherStatusColor as any)[voucherStatus as string];

		return colorToUse;
	}

	private static _getDataValidityString(dateTimestamp: number | string | Date): string {
		const timestamp = normalizeToTimestamp(dateTimestamp);
		const date: Date = new Date(timestamp);
		return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear();
	}

	public static buildLiteralMarkdownElement(literal: string): JSX.Element {
		literal = literal.replace(/&ast;/g, '*');

		const processText = (text: string): JSX.Element => {
			const parts: JSX.Element[] = [];
			const boldRegex = /\*\*(.*?)\*\*/g;
			let lastIndex = 0;
			let match;

			while ((match = boldRegex.exec(text)) !== null) {
				if (match.index > lastIndex) {
					const beforeText = text.slice(lastIndex, match.index);
					parts.push(processUnderline(beforeText));
				}

				// Add bold text (process underlines inside)
				parts.push(<b key={match.index}>{processUnderline(match[1])}</b>);
				lastIndex = boldRegex.lastIndex;
			}

			if (lastIndex < text.length) {
				const remainingText = text.slice(lastIndex);
				parts.push(processUnderline(remainingText));
			}

			return <>{parts}</>;
		};

		const processUnderline = (text: string): JSX.Element => {
			const parts: JSX.Element[] = [];
			const underlineRegex = /__(.*?)__/g;
			let lastIndex = 0;
			let match;

			while ((match = underlineRegex.exec(text)) !== null) {
				if (match.index > lastIndex) {
					parts.push(processLinks(text.slice(lastIndex, match.index), `text-${lastIndex}`));
				}

				parts.push(<u key={match.index}>{processLinks(match[1], `u-${match.index}`)}</u>);
				lastIndex = underlineRegex.lastIndex;
			}

			if (lastIndex < text.length) {
				parts.push(processLinks(text.slice(lastIndex), `text-${lastIndex}`));
			}

			return parts.length > 0 ? <>{parts}</> : processLinks(text, 'span');
		};

		const processLinks = (text: string, keyPrefix: string): JSX.Element => {
			const parts: JSX.Element[] = [];
			const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
			let lastIndex = 0;
			let match;

			while ((match = linkRegex.exec(text)) !== null) {
				if (match.index > lastIndex) {
					parts.push(<span key={`${keyPrefix}-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
				}

				parts.push(
					<a
						key={`${keyPrefix}-${match.index}`}
						href={match[2]}
						target="_blank"
						rel="noopener noreferrer"
						style={{ color: 'inherit', textDecoration: 'underline' }}
					>
						{match[1]}
					</a>,
				);
				lastIndex = linkRegex.lastIndex;
			}

			if (lastIndex < text.length) {
				parts.push(<span key={`${keyPrefix}-${lastIndex}`}>{text.slice(lastIndex)}</span>);
			}

			return parts.length > 0 ? <>{parts}</> : <span>{text}</span>;
		};

		const lines = literal.split('\n');
		const elements = lines.map((line, index) => (
			<React.Fragment key={index}>
				{processText(line)}
				{index < lines.length - 1 && <br />}
			</React.Fragment>
		));

		return <>{elements}</>;
	}
}

export const vwDataRedirect = (
	voucher: NormalizedVoucher,
	navigationService: NavigationServiceV1,
	userInteractionService?: UserInteractionServiceV1,
	cta = voucher.card.cta,
) => {
	const regex = /^(http(s):\/\/.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/;
	if (cta?.src) {
		if (regex.test(cta.src)) {
			window.open(cta.src, '_blank'); // Open in a new tab
		} else if (userInteractionService && cta.src.includes('action:')) {
			actionRedirect(userInteractionService, cta.src);
		} else {
			const target = navigationService.navigateTo(cta.src, {});
			if (target) {
				target.push();
			} else {
				if (cta.src) {
					console.log('detail page not created');
				} else {
					console.log('no detail page provided');
				}
			}
		}
	} else {
		console.log('no detail page provided');
	}
};
